import React from "react";
import { Card } from "@/components/ui/card";
import { Cpu, Search, Eye, Brain, Database, Globe, Activity, Network, CheckCircle } from "lucide-react";

const CoreCapabilitiesSection = () => {
  const capabilities = [
    {
      icon: Cpu,
      title: "Autonomous Decision Engine",
      description: "Drives real-time response actions across your entire environment, automating detection, analysis, and containment through contextual decision-making."
    },
    {
      icon: Search,
      title: "Autonomous Investigation & Response",
      description: "AI-driven workflows autonomously investigate alerts, correlate cross-domain data, and execute containment, reducing mean time to respond (MTTR) from hours to seconds."
    },
    {
      icon: Eye,
      title: "Shadow AI & Adversarial AI Detection",
      description: "Identifies hidden or unauthorized AI tools operating within your environment and neutralizes adversarial attempts to manipulate models or data flows."
    },
    {
      icon: Brain,
      title: "Cognitive Threat Reasoning",
      description: "Built on Spharaka's proprietary LLM, AuraXP reasons contextually, connecting weak signals, user behavior, and environmental data into actionable intelligence."
    },
    {
      icon: Database,
      title: "Organizational Memory",
      description: "Retains the intelligence of your analysts, investigations, and responses, creating a persistent security memory. Even when people move on, AuraXP ensures your defensive knowledge stays."
    },
    {
      icon: Globe,
      title: "Threat Intelligence Fusion",
      description: "Ingests, enriches, and fuses global and organizational threat intelligence feeds into a single contextual knowledge graph, enabling predictive threat detection and correlation."
    },
    {
      icon: Activity,
      title: "Dynamic Risk Scoring",
      description: "Calculates risk in real time for every user, endpoint, and process. AuraXP continuously recalibrates scores based on behavior, exposure, and threat evolution."
    },
    {
      icon: Network,
      title: "Neural Threat Graph",
      description: "Maps relationships across users, systems, and events to visualize and predict threat movement across your ecosystem."
    },
    {
      icon: CheckCircle,
      title: "Trustworthy AI Framework",
      description: "Ensures all AI-driven decisions are explainable, traceable, and compliant, delivering security that's not just intelligent but also accountable."
    }
  ];

  return (
    <section className="py-16 md:py-20 relative bg-cover bg-center bg-no-repeat" style={{ backgroundImage: 'url(/images/core-capabilities-bg.jpg)' }}>
      <div className="absolute inset-0 bg-black/50"></div>
      <div className="container mx-auto px-4 md:px-6 relative z-10">
        <div className="max-w-7xl mx-auto space-y-12">
          <div className="text-center">
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-white mb-4">
              Core Capabilities
            </h2>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {capabilities.map((capability, index) => (
              <Card key={index} className="p-6 hover:shadow-lg transition-shadow bg-white/10 border-2 border-white/20 hover:border-white/40 backdrop-blur-sm">
                <div className="space-y-4">
                  <div className="flex items-center gap-3">
                    <div className="flex-shrink-0">
                      <capability.icon className="w-8 h-8 text-white" />
                    </div>
                    <h3 className="text-xl font-bold text-white">{capability.title}</h3>
                  </div>
                  <p className="text-white/90 leading-relaxed">{capability.description}</p>
                </div>
              </Card>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

export default CoreCapabilitiesSection;
